import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Mic2, Eye, Upload, BarChart3, Users, Heart, Play,
  ArrowRight, Sparkles, CheckCircle2, Flame, Rocket, Compass, BadgeCheck, Crown,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";

const formatNumber = (n) => {
  if (n === null || n === undefined) return "—";
  const x = Number(n);
  if (x >= 1_000_000) return (x / 1_000_000).toFixed(1).replace(/\.0$/, "") + "M";
  if (x >= 1_000)     return (x / 1_000).toFixed(1).replace(/\.0$/, "") + "k";
  return String(x);
};

/**
 * PersonalHero
 * S'affiche en haut de la home SEULEMENT si l'utilisateur est connecté.
 * Variante Artiste → mini-studio avec 4 stats, top-tracks, tips de complétion.
 * Variante Visiteur → hub de découverte avec raccourcis et upgrade CTA.
 * Si non connecté → retourne null (le hero public s'affiche).
 */
export const PersonalHero = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    if (!user?.is_artist) return;
    api.get("/me/studio-stats")
      .then(({ data }) => setStats(data))
      .catch(() => {});
  }, [user]);

  if (!user) return null;

  if (user.is_artist) {
    return <ArtistStudioHero user={user} stats={stats} />;
  }
  return <VisitorDiscoverHero user={user} />;
};

// ========================================================================
//  VARIANTE ARTISTE — Studio dashboard
// ========================================================================
const ArtistStudioHero = ({ user, stats }) => (
  <section className="relative px-4 md:px-12 pt-10 pb-8 max-w-7xl mx-auto" data-testid="studio-hero">
    <div className="relative rounded-3xl border border-brand-red/25 bg-gradient-to-br from-brand-red/10 via-brand-orange/5 to-brand-yellow/5 p-6 md:p-10 overflow-hidden">
      <div className="absolute inset-0 pointer-events-none opacity-[0.07]" style={{
        backgroundImage: "radial-gradient(circle at 20% 20%, #E31B23 0, transparent 50%), radial-gradient(circle at 80% 80%, #EAB308 0, transparent 40%)",
      }} />

      <div className="relative grid lg:grid-cols-[1.1fr_1fr] gap-8 items-start">
        {/* Left: welcome + stats */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <span className="inline-flex items-center gap-1.5 tag-pill bg-brand-red/15 border-brand-red/40 text-brand-red">
              <Mic2 size={12} /> Studio
            </span>
            {user.is_verified && (
              <span className="inline-flex items-center gap-1 tag-pill bg-brand-yellow/15 border-brand-yellow/40 text-brand-yellow text-[10px]">
                <BadgeCheck size={10} /> Vérifié
              </span>
            )}
            {user.is_premium && (
              <span className="inline-flex items-center gap-1 tag-pill bg-brand-orange/15 border-brand-orange/40 text-brand-orange text-[10px]">
                <Crown size={10} /> Premium
              </span>
            )}
          </div>

          <h1 className="font-display text-3xl md:text-5xl font-black tracking-tighter mb-2 text-balance">
            Bienvenue,{" "}
            <span className="bg-gradient-to-r from-[#E31B23] via-brand-orange to-[#EAB308] bg-clip-text text-transparent">
              {user.name?.split(" ")[0] || "artiste"}
            </span>
            .
          </h1>
          <p className="text-white/60 mb-6 max-w-lg">
            Voici ton studio. Publie un nouveau contenu, suis ton audience et booste ton profil.
          </p>

          {/* 4 Stat cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 mb-5" data-testid="studio-stats-cards">
            <StatBox icon={Play}   label="Lectures"  value={formatNumber(stats?.plays_total)} />
            <StatBox icon={Heart}  label="Likes"     value={formatNumber(stats?.likes_total)} />
            <StatBox icon={Users}  label="Followers" value={formatNumber(stats?.followers_count)} />
            <StatBox icon={Mic2}   label="Morceaux"  value={formatNumber(stats?.tracks_count)} />
          </div>

          {/* Primary CTAs */}
          <div className="flex flex-wrap gap-2">
            <Link to="/upload" className="btn-primary inline-flex items-center gap-1.5" data-testid="studio-upload-btn">
              <Upload size={14} /> Publier un contenu
            </Link>
            <Link to="/dashboard" className="btn-ghost inline-flex items-center gap-1.5" data-testid="studio-dashboard-btn">
              <BarChart3 size={14} /> Mon profil & stats
            </Link>
            <Link to={`/artist/${user.user_id}`} className="btn-ghost inline-flex items-center gap-1.5 text-white/70" data-testid="studio-public-view">
              <Eye size={14} /> Vue publique
            </Link>
          </div>
        </div>

        {/* Right: tips + top tracks */}
        <div className="space-y-4">
          {stats?.tips?.length > 0 && (
            <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-4" data-testid="studio-tips">
              <div className="flex items-center gap-2 mb-3">
                <Rocket size={14} className="text-brand-orange" />
                <h3 className="font-display text-sm font-black uppercase tracking-widest">Prochain niveau</h3>
              </div>
              <ul className="space-y-1.5">
                {stats.tips.slice(0, 4).map((t) => (
                  <li key={t.key}>
                    <Link
                      to={t.cta}
                      className="group flex items-center gap-2 text-sm text-white/80 hover:text-white"
                      data-testid={`studio-tip-${t.key}`}
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-brand-orange shrink-0" />
                      <span className="flex-1 truncate">{t.label}</span>
                      <ArrowRight size={12} className="opacity-30 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all" />
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {stats?.top_tracks?.length > 0 && (
            <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-4" data-testid="studio-top-tracks">
              <div className="flex items-center gap-2 mb-3">
                <Flame size={14} className="text-brand-red" />
                <h3 className="font-display text-sm font-black uppercase tracking-widest">Tes tops</h3>
              </div>
              <ul className="space-y-1">
                {stats.top_tracks.slice(0, 3).map((t, i) => (
                  <li key={t.track_id}>
                    <Link
                      to={`/track/${t.track_id}`}
                      className="flex items-center gap-2 p-1.5 rounded-lg hover:bg-white/[0.04]"
                    >
                      <span className="w-5 text-xs font-black text-brand-orange text-right">{i + 1}</span>
                      <span className="flex-1 truncate text-sm">{t.title}</span>
                      <span className="text-[10px] uppercase tracking-wider text-white/40">
                        {formatNumber(t.plays)} ▶
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {stats?.recent_followers?.length > 0 && (
            <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-4" data-testid="studio-recent-followers">
              <div className="flex items-center gap-2 mb-3">
                <Users size={14} className="text-brand-yellow" />
                <h3 className="font-display text-sm font-black uppercase tracking-widest">Nouveaux fans</h3>
              </div>
              <div className="flex flex-wrap gap-2">
                {stats.recent_followers.map((f) => (
                  <Link
                    key={f.user_id}
                    to={`/u/${f.user_id}`}
                    className="flex items-center gap-1.5 rounded-full bg-white/[0.04] border border-white/10 pl-1 pr-2.5 py-0.5 hover:border-brand-yellow/40 transition-colors"
                  >
                    {f.picture ? (
                      <img src={f.picture} alt="" className="w-5 h-5 rounded-full object-cover" />
                    ) : (
                      <div className="w-5 h-5 rounded-full bg-gradient-to-br from-brand-red to-brand-orange flex items-center justify-center text-[9px] font-black">
                        {(f.name || "?").charAt(0).toUpperCase()}
                      </div>
                    )}
                    <span className="text-[11px] font-semibold truncate max-w-[100px]">{f.name}</span>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  </section>
);

// ========================================================================
//  VARIANTE VISITEUR — Hub de découverte
// ========================================================================
const VisitorDiscoverHero = ({ user }) => (
  <section className="relative px-4 md:px-12 pt-10 pb-8 max-w-7xl mx-auto" data-testid="visitor-hero">
    <div className="relative rounded-3xl border border-brand-yellow/25 bg-gradient-to-br from-brand-yellow/10 via-brand-orange/5 to-brand-red/5 p-6 md:p-10 overflow-hidden">
      <div className="absolute inset-0 pointer-events-none opacity-[0.07]" style={{
        backgroundImage: "radial-gradient(circle at 80% 20%, #EAB308 0, transparent 50%), radial-gradient(circle at 20% 80%, #F95738 0, transparent 40%)",
      }} />

      <div className="relative grid lg:grid-cols-[1.2fr_1fr] gap-8 items-center">
        <div>
          <span className="inline-flex items-center gap-1.5 tag-pill bg-brand-yellow/15 border-brand-yellow/40 text-brand-yellow mb-3">
            <Compass size={12} /> Découverte
          </span>

          <h1 className="font-display text-3xl md:text-5xl font-black tracking-tighter mb-2 text-balance">
            Hey{" "}
            <span className="bg-gradient-to-r from-brand-yellow via-brand-orange to-brand-red bg-clip-text text-transparent">
              {user.name?.split(" ")[0] || "toi"}
            </span>
            , explore les talents gasy 🇲🇬
          </h1>
          <p className="text-white/60 mb-6 max-w-lg">
            Tes playlists, tes likes, tes artistes suivis — tout est là. Ou commence par la carte pour voir qui crée depuis ta ville.
          </p>

          <div className="flex flex-wrap gap-2">
            <Link to="/explore" className="btn-primary inline-flex items-center gap-1.5" data-testid="visitor-explore-btn">
              <Compass size={14} /> Explorer
            </Link>
            <Link to="/library" className="btn-ghost inline-flex items-center gap-1.5" data-testid="visitor-library-btn">
              <Heart size={14} /> Ma bibliothèque
            </Link>
            <Link to="/carte" className="btn-ghost inline-flex items-center gap-1.5 text-white/70">
              🛰️ Carte
            </Link>
          </div>
        </div>

        {/* Upgrade to artist CTA */}
        <div className="rounded-2xl border border-brand-red/30 bg-gradient-to-br from-brand-red/10 to-brand-orange/5 p-5" data-testid="visitor-upgrade-cta">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles size={16} className="text-brand-red" />
            <h3 className="font-display text-lg font-black">Tu es artiste ?</h3>
          </div>
          <p className="text-sm text-white/70 mb-4">
            Transforme ton compte en <strong className="text-brand-red">profil artiste</strong> et publie
            tes musiques, clips, spectacles à ta communauté.
          </p>
          <ul className="space-y-1.5 mb-5">
            {[
              "Upload audio & vidéo illimité",
              "Fiche publique + stats",
              "Demande de vérification",
            ].map((perk) => (
              <li key={perk} className="flex items-center gap-2 text-xs text-white/70">
                <CheckCircle2 size={12} className="text-brand-red shrink-0" />
                {perk}
              </li>
            ))}
          </ul>
          <Link
            to="/dashboard"
            className="btn-primary w-full inline-flex items-center justify-center gap-1.5 text-sm"
            data-testid="visitor-become-artist"
          >
            Devenir artiste <ArrowRight size={14} />
          </Link>
        </div>
      </div>
    </div>
  </section>
);

const StatBox = ({ icon: Icon, label, value }) => (
  <div className="rounded-xl border border-white/10 bg-black/20 p-3">
    <Icon size={13} className="text-brand-orange/70 mb-1" />
    <p className="font-display text-2xl font-black leading-none">{value}</p>
    <p className="text-[9px] uppercase tracking-widest text-white/50 mt-1">{label}</p>
  </div>
);

export default PersonalHero;
